'use client'

import { ArrowForward, CheckCircleOutline, EditNoteOutlined, OpenInNew, PlayArrowOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

import useFormatters from '~/data/formatters'

import type { SkipReason } from './draftMapping/shared'
import { type DraftValueLabels, findDraftCurrency, flattenDraftFields, formatDraftValue } from './draftSummary'
import type { BillingDraft, BillingDraftChange, SkippedDraftField } from './types'

export type DraftApplyOutcome =
  | { kind: 'applied'; applied: string[]; skipped: SkippedDraftField[] }
  /** The draft was stashed and the browser is navigating to the wizard that will apply it. */
  | { kind: 'navigated' }
  | { kind: 'failed' }

type BillingDraftCardBaseProps = {
  draft: BillingDraft
  disabled?: boolean
}

/** Inside the composer panel: the draft can be written into the page's form or handed to a wizard. */
type PanelVariantProps = BillingDraftCardBaseProps & {
  variant?: 'panel'
  /**
   * Applies the draft (narrowed to `selectedPaths` when the user unticked changes). The panel decides
   * whether that writes into this page's form or stashes the draft and navigates to a wizard.
   */
  onApply: (selectedPaths: string[] | undefined) => DraftApplyOutcome
  /** True when the draft lands in the form on this page; false when applying navigates to a wizard. */
  appliesInPlace: boolean
}

/**
 * Inside the global assistant bar (contract §8): there is no form on the page, so the primary action
 * always hands the draft to the wizard, and the secondary one asks the assistant to apply it itself.
 */
type BarVariantProps = BillingDraftCardBaseProps & {
  variant: 'bar'
  /** "Review in form": stashes the (narrowed) draft and navigates to the wizard. */
  onReview: (selectedPaths: string[] | undefined) => DraftApplyOutcome
  /** "Ask to apply directly": seeds the chat input. Omitted on read-only surfaces, which hides the button. */
  onAskApply?: () => void
}

type BillingDraftCardProps = PanelVariantProps | BarVariantProps

const SKIP_REASONS: readonly SkipReason[] = [
  'immutableOnEdit',
  'unknownField',
  'unsupportedValue',
  'pricingModelImmutable',
  'notInForm'
]

function isSkipReason(reason: string): reason is SkipReason {
  return (SKIP_REASONS as readonly string[]).includes(reason)
}

/**
 * One proposal from the assistant: what it wants to create or change, why, and the button that puts
 * it into the wizard. Nothing here talks to the API — the user still reviews the form and clicks Save.
 */
export default function BillingDraftCard(props: BillingDraftCardProps) {
  const { draft, disabled } = props
  const isBar = props.variant === 'bar'
  const appliesInPlace = !isBar && props.appliesInPlace
  const t = useTranslations('features.BillingComposer')
  const format = useFormatters()
  const isUpdate = draft.mode === 'update'
  const hasChangeList = isUpdate && draft.changes.length > 0

  const [unselected, setUnselected] = useState<ReadonlySet<string>>(() => new Set())
  const [outcome, setOutcome] = useState<DraftApplyOutcome | null>(null)

  const currency = findDraftCurrency(draft.fields)
  const valueLabels = useMemo<DraftValueLabels>(
    () => ({
      yes: t('value_yes'),
      no: t('value_no'),
      empty: t('value_empty'),
      items: (count) => t('value_items', { count }),
      periods: (rates, periods) => t('value_periods', { rates, periods })
    }),
    [t]
  )
  const formatCurrency = useCallback(
    (major: number) => (currency ? format.currency(major, { currency }) : String(major)),
    [currency, format]
  )
  const renderValue = (path: string, value: unknown) => formatDraftValue(path, value, valueLabels, formatCurrency)

  const fieldRows = useMemo(() => (hasChangeList ? [] : flattenDraftFields(draft.fields)), [draft.fields, hasChangeList])

  const selectedCount = hasChangeList ? draft.changes.filter((change) => !unselected.has(change.path)).length : 0
  const nothingSelected = hasChangeList && selectedCount === 0

  const toggle = (path: string) => {
    setUnselected((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const apply = () => {
    const selectedPaths = hasChangeList
      ? draft.changes.map((change) => change.path).filter((path) => !unselected.has(path))
      : undefined
    setOutcome(props.variant === 'bar' ? props.onReview(selectedPaths) : props.onApply(selectedPaths))
  }

  const applied = outcome?.kind === 'applied'
  const hasBlockingWarning = draft.warnings.some((warning) => warning.severity === 'error')

  return (
    <Card variant="outlined" data-cy="billing-draft-card" sx={{ borderColor: applied ? 'success.main' : undefined }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <EditNoteOutlined fontSize="small" color="action" />
            <Chip size="small" label={t(`entity_${draft.entity}`)} color="primary" variant="outlined" />
            <Chip size="small" label={isUpdate ? t('draft_modeUpdate') : t('draft_modeCreate')} variant="outlined" />
          </Stack>

          <Box>
            <Typography variant="subtitle2">{draft.title || t(`draft_defaultTitle_${draft.entity}`)}</Typography>
            {draft.summary && (
              <Typography variant="body2" color="text.secondary">
                {draft.summary}
              </Typography>
            )}
          </Box>

          {draft.warnings.map((warning) => (
            <Alert key={`${warning.code ?? ''}|${warning.path ?? ''}|${warning.message}`} severity={warning.severity} sx={{ py: 0 }}>
              {warning.message}
              {warning.path && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5, fontFamily: 'monospace' }}>
                  ({warning.path})
                </Typography>
              )}
            </Alert>
          ))}

          {hasChangeList ? (
            <Stack spacing={0.25} data-cy="billing-draft-changes">
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('draft_changesHeading', { count: draft.changes.length })}
              </Typography>
              {draft.changes.map((change) => (
                <ChangeRow
                  key={change.path}
                  change={change}
                  checked={!unselected.has(change.path)}
                  disabled={disabled || applied}
                  onToggle={() => toggle(change.path)}
                  renderValue={renderValue}
                />
              ))}
            </Stack>
          ) : (
            fieldRows.length > 0 && (
              <Stack spacing={0.25} data-cy="billing-draft-fields">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('draft_fieldsHeading', { count: fieldRows.length })}
                </Typography>
                {fieldRows.map((row) => (
                  <Stack key={row.path} direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
                      {row.path}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                      {renderValue(row.path, row.value)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )
          )}

          <Divider />

          {outcome?.kind === 'applied' ? (
            <Stack spacing={1}>
              <Alert severity="success" icon={<CheckCircleOutline fontSize="inherit" />} sx={{ py: 0 }}>
                {t('draft_appliedResult', { count: outcome.applied.length })} {t('draft_reviewHint')}
              </Alert>
              {outcome.skipped.length > 0 && (
                <Alert severity="info" sx={{ py: 0 }} data-cy="billing-draft-skipped">
                  <Typography variant="body2" fontWeight={600}>
                    {t('draft_skippedHeading', { count: outcome.skipped.length })}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {outcome.skipped.map((skip) => (
                      <Typography key={`${skip.path}-${skip.reason}`} component="li" variant="caption">
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                          {skip.path}
                        </Box>
                        {' — '}
                        <Box component="span">{isSkipReason(skip.reason) ? t(`skip_${skip.reason}`) : skip.reason}</Box>
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}
            </Stack>
          ) : outcome?.kind === 'failed' ? (
            <Alert severity="error" sx={{ py: 0 }}>
              {t('draft_applyFailed')}
            </Alert>
          ) : isBar ? (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {hasBlockingWarning ? t('draft_hasErrors') : t('draft_barHint')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Button
                  size="small"
                  variant="contained"
                  onClick={apply}
                  disabled={disabled || nothingSelected || hasBlockingWarning || outcome?.kind === 'navigated'}
                  startIcon={<OpenInNew />}
                  data-cy="billing-draft-review"
                >
                  {t('draft_reviewInForm')}
                </Button>
                {props.onAskApply && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={props.onAskApply}
                    disabled={disabled || hasBlockingWarning || outcome?.kind === 'navigated'}
                    startIcon={<PlayArrowOutlined />}
                    data-cy="billing-draft-ask-apply"
                  >
                    {t('draft_askApply')}
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {hasBlockingWarning ? t('draft_hasErrors') : t('draft_nothingSaved')}
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={apply}
                disabled={disabled || nothingSelected || hasBlockingWarning || outcome?.kind === 'navigated'}
                startIcon={appliesInPlace ? <ArrowForward /> : <OpenInNew />}
                data-cy="billing-draft-apply"
              >
                {appliesInPlace
                  ? hasChangeList
                    ? t('draft_applySelected', { count: selectedCount })
                    : t('draft_apply')
                  : t(`draft_openWizard_${draft.entity}`)}
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

type ChangeRowProps = {
  change: BillingDraftChange
  checked: boolean
  disabled?: boolean
  onToggle: () => void
  renderValue: (path: string, value: unknown) => string
}

function ChangeRow({ change, checked, disabled, onToggle, renderValue }: ChangeRowProps) {
  const hasFrom = change.from !== undefined
  return (
    <Box>
      <FormControlLabel
        sx={{ alignItems: 'flex-start', ml: -1, mr: 0, width: '100%' }}
        control={<Checkbox size="small" checked={checked} disabled={disabled} onChange={onToggle} sx={{ py: 0.25 }} />}
        label={
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {change.label}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
              {hasFrom && (
                <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                  {renderValue(change.path, change.from)}
                </Typography>
              )}
              {hasFrom && <ArrowForward sx={{ fontSize: 12, color: 'text.disabled' }} />}
              <Typography variant="caption" color="text.primary" fontWeight={600}>
                {renderValue(change.path, change.to)}
              </Typography>
            </Stack>
            {change.reason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {change.reason}
              </Typography>
            )}
          </Box>
        }
      />
    </Box>
  )
}
